import { usePlaygroundLocalMsg } from "@faire/prototype-playground/platform/localization";
import { useViewport } from "@faire/react/viewport/useViewport";
import { Close } from "@faire/slate-icons/Close";
import { Button } from "@faire/slate/components/button";
import { IconButton } from "@faire/slate/components/icon-button";
import { tsv } from "@faire/slate/lib/tsv";
import { motion } from "framer-motion";
import { useSyncExternalStore } from "react";
import { createPortal } from "react-dom";

import { SlateStyleOverride } from "../../../components/shared";

interface FavoriteSavedToastProps {
  collectionName: string;
  imageUrl: string;
  onClose: () => void;
  onView: () => void;
}

export const FavoriteSavedToast: React.FC<FavoriteSavedToastProps> = ({
  collectionName,
  imageUrl,
  onClose,
  onView,
}) => {
  const addedMessage = usePlaygroundLocalMsg("Added to {collectionName}", {
    collectionName,
  });
  const viewLabel = usePlaygroundLocalMsg("View");
  const closeLabel = usePlaygroundLocalMsg("Close notification");
  const { isMobile } = useViewport();
  const navigationHeaderBottom = useSyncExternalStore(
    subscribeToNavigationHeaderPosition,
    getNavigationHeaderBottom,
    getServerNavigationHeaderBottom
  );

  return createPortal(
    <div
      className={toastPositionStyle.cls({ isMobile })}
      style={{
        top: isMobile
          ? undefined
          : navigationHeaderBottom + DESKTOP_TOAST_OFFSET_PX,
      }}
    >
      <motion.div
        animate={{ opacity: 1, x: 0 }}
        className={toastStyle.cls()}
        exit={{ opacity: 0, x: 16 }}
        initial={{ opacity: 0, x: 16 }}
        role="status"
        style={{ width: "min(362px, calc(100vw - 32px))" }}
        transition={{ duration: 0.2, ease: "easeOut" }}
      >
        <div className={imageFrameStyle.cls()}>
          <img alt="" className="size-full object-cover" src={imageUrl} />
        </div>
        <div className={toastContentStyle.cls()}>
          <p className="type-fs-paragraph text-fs-text-primary-inverse w-full">
            {addedMessage}
          </p>
          <SlateStyleOverride className="[&_.fs-button--plain]:[--fs-btn-text-color:var(--slate-color-text-primary-inverse)] [&_.fs-button--plain:active]:[--fs-btn-text-color:var(--slate-color-text-primary-inverse)] [&_.fs-button--plain:hover]:[--fs-btn-text-color:var(--slate-color-text-primary-inverse)]">
            <Button onClick={onView} size="small" variant="plain">
              {viewLabel}
            </Button>
          </SlateStyleOverride>
        </div>
        <IconButton
          aria-label={closeLabel}
          Icon={Close}
          onClick={onClose}
          shape="square"
          size="xxSmall"
          variant="primary"
        />
      </motion.div>
    </div>,
    document.body
  );
};

const DESKTOP_TOAST_OFFSET_PX = 24;

const subscribeToNavigationHeaderPosition = (onStoreChange: () => void) => {
  window.addEventListener("scroll", onStoreChange, { passive: true });
  window.addEventListener("resize", onStoreChange);
  return () => {
    window.removeEventListener("scroll", onStoreChange);
    window.removeEventListener("resize", onStoreChange);
  };
};

const getNavigationHeaderBottom = () =>
  Math.max(
    0,
    document.querySelector("header")?.getBoundingClientRect().bottom ?? 0
  );

const getServerNavigationHeaderBottom = () => 0;

const toastPositionStyle = tsv({
  base: "z-[1100]",
  variants: {
    isMobile: {
      true: [
        "fixed right-(--slate-spacing-element-large) bottom-(--slate-spacing-element-large) left-(--slate-spacing-element-large)",
        "flex justify-center",
      ],
      false: "fixed right-(--slate-dimensions-6)",
    },
  },
});

const toastStyle = tsv({
  base: [
    "bg-fs-action-surface-default rounded-fs-component-default shadow-elevation-medium",
    "flex items-start gap-(--slate-spacing-element-large) p-(--slate-spacing-element-large)",
  ],
});

const toastContentStyle = tsv({
  base: [
    "flex min-w-0 flex-1 flex-col items-start justify-center",
    "gap-(--slate-spacing-element-small)",
  ],
});

const imageFrameStyle = tsv({
  base: "rounded-fs-component-default size-12 shrink-0 overflow-hidden",
});
